// Pure helpers behind the contributor's and moderator's earnings, extracted
// from useGetPaid with the same thresholds, fallbacks and messages.
import type { ContributorTestEligibility, ContributorTestLanguageEligibility } from "@/api/tests.api";
import type { BackendPayoutRequest } from "@/lib/types/payments";
import { PAYOUT_RATE_PER_CONTRIBUTION, PAYOUT_THRESHOLD } from "../constants";
import { hasBeenPaidThisMonth, normalizeLanguageKey } from "../helpers";

export interface BackendEligibility {
  step1Reached: boolean;
  step1Remaining: number;
  step1Progress: number;
  hasPendingRequest: boolean;
}

export interface SubmissionInfo {
  status: "PENDING" | "VALIDATED" | "REJECTED";
  submittedAt?: string;
  approvedAt?: string;
  acceptedBaseline?: number;
  totalScore?: number | null;
}

/** Sum of the requests in the given statuses. */
export const sumRequests = (requests: BackendPayoutRequest[], statuses: BackendPayoutRequest["status"][]) =>
  requests.filter((req) => statuses.includes(req.status)).reduce((sum, req) => sum + req.amount, 0);

export interface PayoutStateInput {
  validatedAudios: number;
  pendingAudios: number;
  totalContributions: number;
  testApproved: boolean;
  validatedAudiosAfterTest: number;
  /** PENDING, APPROVED and PAID requests: already taken from the balance. */
  pendingPayoutAmount: number;
  backendEligibility: BackendEligibility | null;
}

export interface PayoutStateCopy {
  reachThresholdMessage: string;
  passValidationTest: string;
  testApproved: string;
  newValidatedContributions: string;
  eligibleEarnPerContribution: string;
}

/** The contributor's two steps (threshold, then test) and what they can claim. */
export function computePayoutState(input: PayoutStateInput, copy: PayoutStateCopy) {
  const { validatedAudios, pendingAudios, totalContributions, testApproved, validatedAudiosAfterTest, pendingPayoutAmount, backendEligibility } = input;
  const step1Reached = backendEligibility?.step1Reached ?? (validatedAudios >= PAYOUT_THRESHOLD);
  const step1Remaining = backendEligibility?.step1Remaining ?? Math.max(0, PAYOUT_THRESHOLD - validatedAudios);
  const step1Progress = backendEligibility?.step1Progress ?? Math.min(100, Math.round((validatedAudios / PAYOUT_THRESHOLD) * 100));

  const step2Passed = step1Reached && testApproved;
  const rawEarnedAmount = step2Passed ? validatedAudiosAfterTest * PAYOUT_RATE_PER_CONTRIBUTION : 0;
  const earnedAmount = Math.max(0, rawEarnedAmount - pendingPayoutAmount);
  const canRequestPayout = step2Passed && earnedAmount > 0;

  let eligibilityStatus = "NOT_ELIGIBLE";
  let statusMessage = copy.reachThresholdMessage
    .replace("{threshold}", String(PAYOUT_THRESHOLD))
    .replace("{remaining}", String(step1Remaining));

  if (step1Reached && !testApproved) {
    eligibilityStatus = "PENDING_TEST";
    statusMessage = copy.passValidationTest;
  } else if (step2Passed && earnedAmount <= 0) {
    eligibilityStatus = "PENDING_TEST";
    statusMessage = copy.testApproved + " " + copy.newValidatedContributions;
  } else if (step2Passed) {
    eligibilityStatus = "ELIGIBLE";
    statusMessage = copy.eligibleEarnPerContribution.replace("{rate}", String(PAYOUT_RATE_PER_CONTRIBUTION));
  }

  return {
    approved: validatedAudios,
    pending: pendingAudios,
    progress: step1Progress,
    remaining: step1Remaining,
    eligible: canRequestPayout,
    earnedAmount,
    remainingAmount: step1Remaining * PAYOUT_RATE_PER_CONTRIBUTION,

    step1Reached,
    step2Passed,
    step1Remaining,
    totalContributions,
    validatedAudios,
    pendingAudios,
    testApproved,
    validatedAudiosAfterTest,
    eligibilityStatus,
    statusMessage,
  };
}

export interface ModeratorPayoutInput {
  contributionsCount?: number;
  userLastPayoutDate?: unknown;
  accumulated: { amount: number; lastPayoutDate?: string };
  monthClosed: boolean;
  moderatorAmount: number;
}

/**
 * The moderator's monthly fee: the server's accumulated amount, or the flat
 * fee once the month is closed and not paid yet.
 */
export function computeModeratorPayoutState(input: ModeratorPayoutInput, referenceDate = new Date()) {
  const validatedCount = input.contributionsCount || 0;
  const lastPayoutDate = input.accumulated.lastPayoutDate || (typeof input.userLastPayoutDate === "string" ? input.userLastPayoutDate : undefined);
  const alreadyPaidThisMonth = hasBeenPaidThisMonth(lastPayoutDate, referenceDate);
  const earnedAmount = input.accumulated.amount > 0
    ? input.accumulated.amount
    : input.monthClosed && !alreadyPaidThisMonth
      ? input.moderatorAmount
      : 0;
  return { validatedCount, lastPayoutDate, alreadyPaidThisMonth, earnedAmount, eligible: earnedAmount > 0 };
}

/**
 * The stats screen's numbers from the three calls (stats, payout situation,
 * eligibility). The eligibility call wins when it answered.
 */
export function summarizeContributorStats(
  stats: any,
  situation: { step1Reached: boolean; step1Remaining: number; step1Progress: number; hasPendingPayoutRequest: boolean; hasValidatedTest: boolean; validatedAudiosAfterTest: number } | null,
  eligibility: ContributorTestEligibility | null,
) {
  const total = Number(stats?.global?.total ?? 0);
  const uploaded = Number(stats?.audio?.uploaded ?? 0);
  const accepted = Number(stats?.audio?.accepted ?? 0);
  const rejected = Number(stats?.audio?.rejected ?? 0);
  const pending = Math.max(0, uploaded - accepted - rejected);

  const languages = stats?.languages && typeof stats.languages === "object" ? stats.languages : {};
  const acceptedByLanguageFromStats = Object.entries(languages).reduce((acc, [lang, payload]) => {
    const source = payload as any;
    const acceptedForLanguage = Number(source?.audioAccepted ?? source?.accepted ?? source?.audio?.accepted ?? source?.validated ?? 0);
    acc[normalizeLanguageKey(lang)] = Number.isFinite(acceptedForLanguage) ? acceptedForLanguage : 0;
    return acc;
  }, {} as Record<string, number>);
  const acceptedByLanguage = eligibility
    ? eligibility.languages.reduce((acc, entry) => {
        acc[normalizeLanguageKey(entry.language)] = entry.validatedAudioCount;
        return acc;
      }, {} as Record<string, number>)
    : acceptedByLanguageFromStats;

  let backend: { eligibility: BackendEligibility; testApproved: boolean; validatedAudiosAfterTest: number } | null = null;
  if (situation) {
    const bestLanguage = eligibility?.languages.reduce(
      (best: ContributorTestLanguageEligibility | null, entry: ContributorTestLanguageEligibility) =>
        !best || entry.progress > best.progress ? entry : best,
      null,
    );
    backend = {
      eligibility: {
        step1Reached: bestLanguage?.eligible ?? situation.step1Reached,
        step1Remaining: bestLanguage?.remaining ?? situation.step1Remaining,
        step1Progress: bestLanguage?.progress ?? situation.step1Progress,
        hasPendingRequest: situation.hasPendingPayoutRequest,
      },
      testApproved: situation.hasValidatedTest,
      validatedAudiosAfterTest: situation.validatedAudiosAfterTest,
    };
  }

  return {
    totalContributions: total,
    validatedAudios: eligibility?.validatedAudioCount ?? accepted,
    pendingAudios: pending,
    acceptedByLanguage,
    backend,
  };
}

/** The user's own test submissions, keyed by language (the last one wins). */
export function buildSubmissionMap(items: any[]): Record<string, SubmissionInfo> {
  const map: Record<string, SubmissionInfo> = {};
  for (const it of items) {
    const lang = normalizeLanguageKey(it.language || it.test?.language || it.Test?.language);
    const submittedAt = it.createdAt;
    const status: SubmissionInfo["status"] = it.status === "VALIDATED" ? "VALIDATED" : it.status === "REJECTED" ? "REJECTED" : "PENDING";
    if (status === "PENDING") {
      map[lang] = { status, submittedAt, approvedAt: undefined, acceptedBaseline: undefined, totalScore: null };
    } else {
      const approvedAt = it.validatedAt || it.updatedAt || it.createdAt;
      const baselineRaw = Number(it.acceptedCount ?? it.audioAccepted ?? it.validatedAudioContributionCount ?? it.acceptedAtValidation ?? NaN);
      const acceptedBaseline = Number.isFinite(baselineRaw) ? baselineRaw : undefined;
      const scoreRaw = it.earnedPoints ?? it.totalScore ?? it.score ?? null;
      const parsedScore = typeof scoreRaw === "string" ? parseFloat(scoreRaw) : scoreRaw;
      const totalScore = Number.isFinite(parsedScore) ? parsedScore : null;
      map[lang] = { status, submittedAt, approvedAt, acceptedBaseline, totalScore };
    }
  }
  return map;
}

/** The language with the best progress towards the test (first one on a tie). */
export function topEligibilityLanguage(byLanguage: Record<string, ContributorTestLanguageEligibility>): string | null {
  const entries = Object.entries(byLanguage);
  if (!entries.length) return null;
  const [topLang] = entries.reduce((best, cur) => (cur[1].progress > best[1].progress ? cur : best));
  return topLang;
}

/** Eligibility entries keyed by normalized language code. */
export function eligibilityByLanguage(eligibility: ContributorTestEligibility | null) {
  const entries = eligibility?.languages ?? [];
  return entries.reduce((map, entry) => {
    map[normalizeLanguageKey(entry.language)] = entry;
    return map;
  }, {} as Record<string, ContributorTestLanguageEligibility>);
}
